import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, CheckCircle, ClipboardList } from 'lucide-react'
import { useUser } from '../contexts/UserContext'
import { getMyArchivedSupplyRequests, SupplyRequest } from '../api/supplyRequests'

const ArchivedFoodBankRequestsPage = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useUser()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [requests, setRequests] = useState<SupplyRequest[]>([])

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    if (user?.userType !== 'FOOD_BANK') {
      navigate('/')
      return
    }
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await getMyArchivedSupplyRequests()
        setRequests(data)
      } catch (e: any) {
        setError(e?.message || 'Failed to load archived requests')
      } finally {
        setLoading(false)
      }
    })()
  }, [isAuthenticated, user?.userType, navigate])

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate('/dashboard')} className="btn-outline inline-flex items-center mb-3">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Archived Requests</h1>
            <p className="text-sm text-gray-600 mt-1">Requests you’ve taken down stay accessible here.</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-800 font-medium">Failed to load archived requests</p>
            <p className="text-xs text-red-700 mt-1">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ) : requests.length === 0 ? (
            <div className="p-10 text-center">
              <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-3" />
              <p className="text-sm text-gray-700 font-medium">No archived requests yet</p>
              <p className="text-xs text-gray-500 mt-1">Archived requests will show up here.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {requests.map((r) => (
                <div key={r.id} className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{r.title}</p>
                    <p className="text-xs text-gray-600 truncate">{r.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Status: <span className="font-medium">{r.status}</span>
                    </p>
                  </div>
                  <Link to={`/delivery/${r.id}`} className="btn-outline inline-flex items-center flex-shrink-0">
                    <ClipboardList className="h-4 w-4 mr-2" />
                    View
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ArchivedFoodBankRequestsPage


